import { SupabaseClient, createClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// Get these from the client.ts file to avoid duplication
const SUPABASE_URL = "https://nwweejpidselzqtwvgnb.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im53d2VlanBpZHNlbHpxdHd2Z25iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI5NDc5ODgsImV4cCI6MjA1ODUyMzk4OH0.ys35Bxn1zBgzOwLus_43SaNVU79fkpKtOMiHmYg0ekc";

interface AdminAuthData {
  authenticated: boolean;
  sessionId: string;
  loginTime: number;
}

/**
 * Get the current admin authentication data from localStorage
 */
export function getAdminAuthData(): AdminAuthData | null {
  const authData = localStorage.getItem('admin_auth_data');
  if (!authData) return null;

  try {
    const data = JSON.parse(authData) as AdminAuthData;
    
    // Validate the session hasn't expired (24 hours)
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    
    if (data.authenticated && data.sessionId && data.loginTime && (now - data.loginTime < twentyFourHours)) {
      return data;
    }
  } catch (err) {
    console.error('Error parsing admin auth data:', err);
  }
  
  return null;
}

/**
 * Check if the current user is authenticated as admin
 */
export function isAdminAuthenticated(): boolean {
  return getAdminAuthData() !== null;
}

/**
 * Get a Supabase client with admin headers if authenticated
 */
export function getAdminClient(): SupabaseClient<Database> {
  const authData = getAdminAuthData();
  if (!authData) return supabase;

  // Create a new client with admin headers
  return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    global: {
      headers: {
        'x-admin-session': authData.sessionId
      }
    }
  });
}

/**
 * Wrapper function to ensure admin authentication before executing an operation
 * @param operation The operation to execute with admin client
 * @returns The result of the operation or null if not authenticated
 */
export async function withAdminClient<T>(
  operation: (client: SupabaseClient<Database>) => Promise<T>
): Promise<T | null> {
  if (!isAdminAuthenticated()) {
    console.error('Attempted to perform admin operation without authentication');
    return null;
  }

  try {
    const adminClient = getAdminClient();
    return await operation(adminClient);
  } catch (error) {
    console.error('Admin operation failed:', error);
    return null;
  }
} 